/**
 * Selection identity for a search choice — the ONE key every comparison site in
 * ChannelSearch uses, so the toggle, the dedupe, the dropdown's selected state and the
 * React keys cannot drift apart.
 *
 * [WAT-11767] The original problem: identity was the display text alone. Channel display
 * names are NOT unique, so two distinct channels collapsed into one selection, and clicking
 * the SECOND same-named channel read as clicking the first and removed its chip.
 *
 * [WAT-11775] Why the name + channel-number pair was not enough either. WAT-11771 established
 * that the repo's real channel identity is the `uuid`:
 *   - a channel NUMBER is a tuner POSITION that distinct channels legitimately share — 11
 *     numbers are shared on the live feed;
 *   - display NAMES are not unique, which is what WAT-11767 was about;
 *   - so the PAIR is not unique either. The live feed carries two rows both named
 *     "Sports Mix" both on 205 (`Sports Mix-205-438` / `Sports Mix-205-450`). Keyed on the
 *     pair those are ONE selection — the exact WAT-11767 defect, one level deeper.
 * `matchId` is minted once per channel (channel-lineup-parser server-side,
 * buildLocalRsnChannel for local/RSN), is unique across every row, and survives the
 * national-alias rename. With both this key and the grid's `channelMatchesSearchChoice`
 * preferring it, chip and grid agree.
 *
 * The tiers, in order:
 *   1. `matchId`    — the feed identity, stamped by the channel-lineup parser and carried
 *                     onto choices by getSearchChoices. This is the SAME field
 *                     `channelMatchesSearchChoice` compares, which is what makes the chip
 *                     list and the grid agree. (An earlier draft of this file keyed on
 *                     `uuid`; that is the DISPLAY key, which the CHLUP grid builders
 *                     overwrite per bucket, so it is not comparable across the two sides —
 *                     see WAT-11771.)
 *   2. `resourceId` — an OV "Shows & Movies" choice. These carry no channel number and set
 *                     `displayText` from the title, so two same-titled resources (a remake
 *                     pair) keyed identically and clicking the second removed the first: the
 *                     WAT-11767 defect verbatim, in the third choice shape. `resourceId` is
 *                     already the identity for search-handlers and SearchResultBanner.
 *   3. label + number — the fallback. The label is `displayText ?? name`, because a
 *                     SUBSCRIPTION choice carries `name` and no `displayText` (an
 *                     embeddedSvods entry's fields are name / icon / indicators /
 *                     chnlPackages). Reading only `displayText` bucketed every subscription
 *                     into the single key `"|"`.
 *
 * 🔴 The previous version of this comment claimed "every ChlupStore getSearchChoices choice
 * degrades to a name-only key". That stopped being true when #8065 made getSearchChoices
 * stamp `chnlNum`, and is doubly untrue now that it stamps `uuid`.
 *
 * The label is trimmed and lowercased so a key cannot differ by casing alone, and each tier
 * is prefixed so the three namespaces can never collide with one another.
 */
export interface SearchChoiceLike {
  /**
   * The FEED identity, stamped by the channel-lineup parser and carried onto search choices
   * by ChlupStore's getSearchChoices. Deliberately NOT `uuid`: `uuid` is the DISPLAY key and
   * the CHLUP grid builders overwrite it per bucket (`Included-…`, `AddOn-…`), so it is not
   * comparable across the two sides. `matchId` is what `channelMatchesSearchChoice` compares.
   */
  matchId?: string;
  /** Display/React key. Present on grid rows, where it may be a bucket-prefixed value. */
  uuid?: string;
  resourceId?: string | number;
  displayText?: string;
  name?: string;
  chnlNum?: string | number;
}

/** Trim; `null`/`undefined` collapse to "". */
const str = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
};

/**
 * The label a choice is displayed and filtered by: `displayText` where the producer set one,
 * `name` otherwise. The subscription shape only ever has `name`.
 */
export const getChoiceLabel = (choice?: SearchChoiceLike | null): string => str(choice?.displayText) || str(choice?.name);

export const getChoiceKey = (choice?: SearchChoiceLike | null): string => {
  const matchId = str(choice?.matchId);
  if (matchId) {
    return `id:${matchId}`;
  }
  const resourceId = str(choice?.resourceId);
  if (resourceId) {
    return `ov:${resourceId}`;
  }
  return `chnl:${getChoiceLabel(choice).toLowerCase()}|${str(choice?.chnlNum)}`;
};

/**
 * [WAT-11775] Narrow already-name-filtered rows down to the ones the selection actually
 * identifies.
 *
 * The compare tool's grid filter lives in the channel-lineup-provider package and matches
 * `channel.name` against the selected choices' names. WAT-11767 made a new state reachable —
 * exactly ONE of two same-named channels selected — and in that state the grid renders BOTH
 * rows while the chip list shows one, with no selection that shows only one. This closes the
 * gap on the consumer side, since the provider's matcher is not ours to change.
 *
 * Deliberately conservative:
 *   • Only labels for which the selection carries a distinguishing identity (a `uuid` or a
 *     `chnlNum`) are considered at all. Everything else passes through untouched.
 *   • It FAILS OPEN per label: if no row for a label matches by identity, every row for that
 *     label is kept. Showing what the old behaviour showed beats blanking the grid because a
 *     `chnlNum` differed in format between two feeds.
 */
export const narrowRowsToSelectedIdentity = <T extends SearchChoiceLike>(
  rows: T[] | undefined,
  selectedChoices: SearchChoiceLike[] | undefined
): T[] | undefined => {
  const selected = (selectedChoices || [])?.filter(Boolean);
  if (!Array.isArray(rows) || rows?.length === 0 || selected?.length === 0) {
    return rows;
  }
  const selectedKeys = new Set(selected?.map(choice => getChoiceKey(choice)));
  const labelsWithIdentity = new Set(
    selected
      ?.filter(choice => str(choice?.matchId) || str(choice?.chnlNum))
      ?.map(choice => getChoiceLabel(choice)?.toLowerCase())
      ?.filter(Boolean)
  );
  if (labelsWithIdentity?.size === 0) {
    return rows;
  }
  // Per-label fail-open: a label is only narrowed when at least one of its rows is actually
  // identified by the selection.
  const narrowableLabels = new Set(
    [...labelsWithIdentity]?.filter(label => rows?.some(
      row => getChoiceLabel(row)?.toLowerCase() === label && selectedKeys?.has(getChoiceKey(row))
    ))
  );
  if (narrowableLabels?.size === 0) {
    return rows;
  }
  return rows?.filter(row => {
    const label = getChoiceLabel(row)?.toLowerCase();
    if (!narrowableLabels?.has(label)) {
      return true;
    }
    return selectedKeys?.has(getChoiceKey(row));
  });
};
